/**
 * VitalsStrip
 *
 * Compact 4-card metric strip for the top of PatientChart.
 * Each card: micro-label, large numeric value, unit, delta vs prior, range hint.
 *
 * Data-flexible: accepts a generic VitalMetric[]. Caller decides which metrics to
 * show — BMI/BP from Patient, A1C/TSH from labs once wired. Renders nothing if no
 * metrics resolve, so it's safe to drop in for any patient.
 */

import React from 'react';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export type RangeStatus = 'in-range' | 'above' | 'below' | 'unknown';
export type DeltaDirection = 'down' | 'up' | 'flat';

export interface VitalMetric {
  label: string;
  value: string | number | null | undefined;
  unit?: string;
  /** Short delta string, e.g. "0.4 vs 30d" */
  delta?: string;
  deltaDirection?: DeltaDirection;
  /** Whether the delta direction is clinically favorable (drives color). */
  deltaFavorable?: boolean;
  rangeStatus?: RangeStatus;
  rangeLabel?: string;
}

interface VitalsStripProps {
  metrics: VitalMetric[];
  className?: string;
}

const RANGE_STYLES: Record<RangeStatus, string> = {
  'in-range': 'text-emerald-600',
  above: 'text-amber-600',
  below: 'text-amber-600',
  unknown: 'text-slate-400',
};

const DELTA_ICON: Record<DeltaDirection, React.ElementType> = {
  down: TrendingDown,
  up: TrendingUp,
  flat: Minus,
};

const VitalsStrip: React.FC<VitalsStripProps> = ({ metrics, className }) => {
  const visible = metrics.filter((m) => m.value !== null && m.value !== undefined && m.value !== '');
  // Only render strip with 2+ metrics (single metrics live in left panel header)
  if (visible.length < 2) return null;

  return (
    <div
      className={cn(
        'grid gap-2 sm:grid-cols-2 lg:grid-cols-4',
        className,
      )}
    >
      {visible.map((m) => {
        const Icon = m.deltaDirection ? DELTA_ICON[m.deltaDirection] : null;
        const deltaTone = m.deltaFavorable === false
          ? 'text-amber-600'
          : m.deltaFavorable === true
            ? 'text-emerald-600'
            : 'text-slate-500';
        const rangeTone = RANGE_STYLES[m.rangeStatus ?? 'unknown'];
        return (
          <div
            key={m.label}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {m.label}
            </p>
            <div className="mt-0.5 flex items-baseline gap-1">
              <span className="text-2xl font-semibold text-slate-900 leading-none">
                {m.value}
              </span>
              {m.unit && (
                <span className="text-xs text-slate-500">{m.unit}</span>
              )}
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px]">
              {m.delta ? (
                <span className={cn('flex items-center gap-0.5', deltaTone)}>
                  {Icon && <Icon className="h-3 w-3" />}
                  {m.delta}
                </span>
              ) : (
                <span />
              )}
              {m.rangeLabel && (
                <span className={cn('font-medium', rangeTone)}>
                  {m.rangeLabel}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VitalsStrip;
